import { Link } from "react-router-dom";
import { Feather, Heart, Sparkles, MapPin, Users } from "lucide-react";
import KidsIllustration from "@/components/KidsIllustration";

export default function About() {
  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14" data-testid="about-page">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5">
          <div
            className="relative rounded-[2.5rem] p-8 lg:p-10 overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,107,157,0.18) 0%, rgba(110,196,232,0.20) 55%, rgba(180,159,255,0.22) 100%)",
              border: "1px solid rgba(180,159,255,0.35)",
              boxShadow: "0 20px 50px rgba(45,48,85,0.10)",
            }}
          >
            <Feather size={30} style={{ color: "#FF6B9D" }} />
            <p className="font-caveat text-2xl mt-3" style={{ color: "#FF6B9D" }}>a small hello from</p>
            <h2 className="font-fredoka text-5xl leading-tight mt-1" style={{ color: "#2D3055" }}>
              M.K. Bunch
            </h2>
            <p className="mt-3 font-nunito text-sm" style={{ color: "#4A4E6D" }}>
              Author · Wife · Mom of four · Washington, D.C.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-fredoka"
                style={{ background: "#FFFFFF", color: "#2D3055", border: "1px solid rgba(180,159,255,0.35)" }}>
                <Sparkles size={12} style={{ color: "#FFD93D" }} /> whimsical
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-fredoka"
                style={{ background: "#FFFFFF", color: "#2D3055", border: "1px solid rgba(180,159,255,0.35)" }}>
                <Heart size={12} style={{ color: "#FF6B9D" }} /> tender
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-fredoka"
                style={{ background: "#FFFFFF", color: "#2D3055", border: "1px solid rgba(180,159,255,0.35)" }}>
                <Feather size={12} style={{ color: "#6EC4E8" }} /> adventurous
              </span>
            </div>
          </div>
          <div className="mt-8 paper-card p-5">
            <p className="font-caveat text-2xl leading-tight" style={{ color: "#2D3055" }}>
              "Write the book you needed when you were small."
            </p>
            <p className="mt-2 text-sm font-nunito" style={{ color: "#7E82A1" }}>— a note pinned above my desk</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          <span className="font-caveat text-3xl" style={{ color: "#FF6B9D" }}>hello, hello</span>
          <h1 className="font-fredoka text-5xl sm:text-6xl mt-2" style={{ color: "#2D3055" }}>
            Meet M.K. Bunch
          </h1>
          <div className="mt-5 flex flex-wrap gap-2 text-sm font-nunito">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: "#E7EEFF", color: "#2D3055", border: "1px solid #C7B5FF" }}>
              <MapPin size={14} style={{ color: "#FF6B9D" }} /> Washington, D.C.
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: "#E7EEFF", color: "#2D3055", border: "1px solid #C7B5FF" }}>
              <Users size={14} style={{ color: "#6EC4E8" }} /> Wife &amp; Mom of 4
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
              style={{ background: "#E7EEFF", color: "#2D3055", border: "1px solid #C7B5FF" }}>
              <Feather size={14} style={{ color: "#FF8C42" }} /> Children's book author
            </span>
          </div>

          <div className="mt-8 prose-warm font-nunito text-lg">
            <p>
              Hi, I'm <span className="font-fredoka" style={{ color: "#2D3055" }}>M.K. Bunch</span> — an African American author,
              wife, and Mom of four writing whimsical children's books from Washington, D.C.
            </p>
            <p>
              I have loved children's books for as long as I can remember. The way a picture book can
              take a small hand and lead it, page by colorful page, into a world where anything is possible —
              it's the closest thing I know to magic. I write to give my children (and yours) stories full
              of <span className="font-fredoka" style={{ color: "#FF6B9D" }}>adventure</span>,{" "}
              <span className="font-fredoka" style={{ color: "#6EC4E8" }}>discovery</span>, and the quiet, steady
              reminder that the wonders of life are already inside them.
            </p>
            <p>
              My books celebrate children of every home and every background. Because every child
              deserves to open a book and see themselves shine back — brave, curious, silly, and wildly,
              wonderfully loved.
            </p>
            <p>
              When I'm not at my writing desk, you'll find me chasing my four little wonders around D.C. —
              usually with sticky snacks in my bag and a story half-forming in my head.
            </p>
          </div>

          <div className="mt-10 grid sm:grid-cols-3 gap-4">
            <div className="warm-card p-5">
              <Feather size={22} style={{ color: "#6EC4E8" }} />
              <p className="font-fredoka text-lg mt-2" style={{ color: "#2D3055" }}>Cozy writing</p>
              <p className="text-sm font-nunito mt-1" style={{ color: "#4A4E6D" }}>Warm words, gentle rhythm, honest heart.</p>
            </div>
            <div className="warm-card p-5">
              <Sparkles size={22} style={{ color: "#FF6B9D" }} />
              <p className="font-fredoka text-lg mt-2" style={{ color: "#2D3055" }}>Big adventures</p>
              <p className="text-sm font-nunito mt-1" style={{ color: "#4A4E6D" }}>Curiosity, discovery, and the wonders of life.</p>
            </div>
            <div className="warm-card p-5">
              <Heart size={22} style={{ color: "#FF8C42" }} />
              <p className="font-fredoka text-lg mt-2" style={{ color: "#2D3055" }}>Every little heart</p>
              <p className="text-sm font-nunito mt-1" style={{ color: "#4A4E6D" }}>Stories for children of every background.</p>
            </div>
          </div>

          <div className="mt-10 warm-card p-6 grid sm:grid-cols-5 gap-6 items-center">
            <div className="sm:col-span-2">
              <KidsIllustration className="w-full h-auto rounded-2xl" />
            </div>
            <div className="sm:col-span-3">
              <p className="font-caveat text-2xl" style={{ color: "#FF6B9D" }}>a small hope</p>
              <p className="font-fredoka text-xl mt-1" style={{ color: "#2D3055" }}>
                That every child who opens my books feels seen, brave, and utterly delighted to be themselves.
              </p>
            </div>
          </div>

          <Link to="/contact" className="btn-terracotta mt-10 inline-flex" data-testid="about-contact-cta">
            Say hello →
          </Link>
        </div>
      </div>
    </div>
  );
}
